import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ConditionGroup } from "./ConditionGroup";

interface CriteriaCondition {
  field: string;
  operator: string;
  value: string;
}

interface CriteriaGroup {
  logic: "and" | "or";
  conditions: CriteriaCondition[];
}

interface TagCriteriaBuilderProps {
  value: any;
  onChange: (criteria: any) => void;
}

interface CriteriaStructure {
  groups: CriteriaGroup[];
  operators: ("and" | "or")[]; // operators between groups
}

export function TagCriteriaBuilder({ value, onChange }: TagCriteriaBuilderProps) {
  // Initialize with either the provided value or a default structure
  const [criteria, setCriteria] = useState<CriteriaStructure>(() => {
    if (value && (value.and || value.or)) {
      // Convert old format to new structure
      const logic = value.and ? "and" : "or";
      const conditions = value.and || value.or;
      return {
        groups: [{
          logic,
          conditions: conditions.map((c: any) => ({
            field: c.field || "",
            operator: c.operator || "",
            value: c.value || ""
          }))
        }],
        operators: []
      };
    }
    return {
      groups: [{
        logic: "and",
        conditions: [{ field: "", operator: "", value: "" }]
      }],
      operators: []
    };
  });

  const updateCriteria = (newCriteria: CriteriaStructure) => {
    setCriteria(newCriteria);
    
    // Convert to backend format
    const validGroups = newCriteria.groups
      .map(group => ({
        [group.logic]: group.conditions.filter(
          condition => condition.field && condition.operator && condition.value
        ).map(condition => ({
          field: condition.field,
          operator: condition.operator,
          value: condition.value,
        }))
      }))
      .filter(group => Object.values(group)[0].length > 0);

    // Handle complex nested logic
    if (validGroups.length <= 1) {
      return onChange(validGroups[0] || {});
    }

    // Build nested structure based on operators
    let result: any = validGroups[0];
    for (let i = 1; i < validGroups.length; i++) {
      const operator = newCriteria.operators[i - 1] || "or";
      result = { [operator]: [result, validGroups[i]] };
    }
    
    onChange(result);
  };

  const addGroup = () => {
    const newCriteria = {
      ...criteria,
      groups: [
        ...criteria.groups,
        {
          logic: "and" as const,
          conditions: [{ field: "", operator: "", value: "" }]
        }
      ],
      operators: [...criteria.operators, "or" as const] // Default to OR for new groups
    };
    updateCriteria(newCriteria);
  };

  const removeGroup = (groupIndex: number) => {
    if (criteria.groups.length === 1) return;
    const newGroups = criteria.groups.filter((_, i) => i !== groupIndex);
    const newOperators = criteria.operators.filter((_, i) => 
      groupIndex === 0 ? i !== 0 : i !== groupIndex - 1
    );
    const newCriteria = {
      groups: newGroups,
      operators: newOperators
    };
    updateCriteria(newCriteria);
  };

  const updateGroupLogic = (groupIndex: number, logic: "and" | "or") => {
    const newGroups = [...criteria.groups];
    newGroups[groupIndex] = { ...newGroups[groupIndex], logic };
    updateCriteria({ ...criteria, groups: newGroups });
  };

  const updateGroupConditions = (groupIndex: number, conditions: CriteriaCondition[]) => {
    const newGroups = [...criteria.groups];
    newGroups[groupIndex] = { ...newGroups[groupIndex], conditions };
    updateCriteria({ ...criteria, groups: newGroups });
  };

  const updateOperator = (operatorIndex: number, operator: "and" | "or") => {
    const newOperators = [...criteria.operators];
    newOperators[operatorIndex] = operator;
    updateCriteria({ ...criteria, operators: newOperators });
  };

  const generatePreview = () => {
    const groupPreviews = criteria.groups.map((group, index) => {
      const validConditions = group.conditions.filter(c => c.field && c.operator && c.value);
      if (validConditions.length === 0) return null;
      
      const conditionText = validConditions.length === 1 
        ? "1 condition"
        : `${group.logic === "and" ? "all" : "any"} of ${validConditions.length} conditions`;
      
      return `Group ${index + 1} (${conditionText})`;
    }).filter(Boolean);

    if (groupPreviews.length === 0) return "No valid conditions set";
    if (groupPreviews.length === 1) return `Devices matching ${groupPreviews[0]}`;
    
    // Build preview with individual operators
    let preview = groupPreviews[0];
    for (let i = 1; i < groupPreviews.length; i++) {
      const operator = criteria.operators[i - 1] || "OR";
      preview += ` ${operator.toUpperCase()} ${groupPreviews[i]}`;
    }
    return `Devices matching ${preview}`;
  };

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-base font-medium">When should devices get this tag?</Label>
        <p className="text-sm text-muted-foreground mt-1">
          Create groups of conditions and combine them with AND/OR logic
        </p>
      </div>

      <div className="space-y-4">
        {criteria.groups.map((group, groupIndex) => (
          <div key={groupIndex} className="space-y-3">
            <ConditionGroup
              groupIndex={groupIndex}
              logic={group.logic}
              conditions={group.conditions}
              onLogicChange={(logic) => updateGroupLogic(groupIndex, logic)}
              onConditionsChange={(conditions) => updateGroupConditions(groupIndex, conditions)}
              onRemoveGroup={() => removeGroup(groupIndex)}
              canRemoveGroup={criteria.groups.length > 1}
            />
            
            {groupIndex < criteria.groups.length - 1 && (
              <div className="flex items-center justify-center">
                <Select 
                  value={criteria.operators[groupIndex] || "or"} 
                  onValueChange={(value: "and" | "or") => updateOperator(groupIndex, value)}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="and">AND</SelectItem>
                    <SelectItem value="or">OR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addGroup}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Another Group
      </Button>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Rule preview:</strong> {generatePreview()}
        </p>
      </div>
    </div>
  );
}